// The application's context provider stack. This module is composition only:
// it owns no state and performs no data loading of its own.

import type {ReactNode} from 'react';
import {QueryClientProvider} from '@tanstack/react-query';

import {appQueryClient} from './data/queryClient.js';
import {AuthProvider} from '../contexts/AuthContext.jsx';
import {UserDirectoryProvider} from '../shared/userDirectoryData.js';
import {BatchesProvider} from '../contexts/BatchesContext.jsx';
import {PigProvider} from '../contexts/PigContext.jsx';
import {LayerProvider} from '../contexts/LayerContext.jsx';
import {DailysRecentProvider} from '../contexts/DailysRecentContext.jsx';
import {WebformsConfigProvider} from '../contexts/WebformsConfigContext.jsx';
import {FeedCostsProvider} from '../contexts/FeedCostsContext.jsx';
import {UIProvider} from '../contexts/UIContext.jsx';
import {ConfirmProvider} from '../contexts/ConfirmContext.jsx';
import {ToastProvider} from '../shared/Toast.tsx';

import {todayISO} from '../lib/dateUtils.js';
import {pigBreedingTimelineRange} from '../lib/pigBreedingTimelineRange.js';
import {DEFAULT_WEBFORMS_CONFIG} from '../lib/defaults.js';
import {INITIAL_BREEDERS, INITIAL_FARROWING} from './data/pigSeedData.js';
import {EMPTY_FORM} from './data/broilerFormDefaults.js';

// Pig breeding timeline default start: thirteen rolling calendar months back.
// Lazy initializer — PigProvider calls it once, on first render only.
const breedTlStartInit = () => pigBreedingTimelineRange(todayISO()).start;

type AppProvidersProps = {
  children: ReactNode;
};

export default function AppProviders({children}: AppProvidersProps) {
  return (
    <QueryClientProvider client={appQueryClient}>
      <AuthProvider>
        <UserDirectoryProvider>
          <BatchesProvider formInit={EMPTY_FORM}>
            <PigProvider
              initialFarrowing={INITIAL_FARROWING}
              initialBreeders={INITIAL_BREEDERS}
              breedTlStartInit={breedTlStartInit}
            >
              <LayerProvider>
                <DailysRecentProvider>
                  <WebformsConfigProvider configInit={DEFAULT_WEBFORMS_CONFIG}>
                    <FeedCostsProvider>
                      <UIProvider>
                        <ConfirmProvider>
                          <ToastProvider>{children}</ToastProvider>
                        </ConfirmProvider>
                      </UIProvider>
                    </FeedCostsProvider>
                  </WebformsConfigProvider>
                </DailysRecentProvider>
              </LayerProvider>
            </PigProvider>
          </BatchesProvider>
        </UserDirectoryProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}
